import { createContext, type PropsWithChildren, useContext, useState } from 'react'
import { LocalStorageKeys } from '@/constants/localstorage'
import {
  getUserScopedItem,
  removeUserScopedItem,
  setUserScopedItem,
} from '@/utils/userScopedStorage'

export type DefaultTradingMode = 'last-used' | 'cash' | 'isolated'

interface SettingsContextProps {
  hotkeysEnabled: boolean
  soundEnabled: boolean
  defaultTradingMode: DefaultTradingMode
  rememberSelectedLeverage: boolean
  showOrdersAndEntriesOnChart: boolean
  setHotkeysEnabled: (enabled: boolean) => void
  setSoundEnabled: (enabled: boolean) => void
  setDefaultTradingMode: (mode: DefaultTradingMode) => void
  setRememberSelectedLeverage: (enabled: boolean) => void
  setShowOrdersAndEntriesOnChart: (enabled: boolean) => void
}

const SettingsContext = createContext<SettingsContextProps | undefined>(undefined)

const SettingsProvider = ({ children }: PropsWithChildren) => {
  const [hotkeysEnabled, setHotkeysEnabledState] = useState<boolean>(() => {
    const value = getUserScopedItem(LocalStorageKeys.HOTKEYS_ENABLED)
    return value === null ? true : value === 'true'
  })
  const [soundEnabled, setSoundEnabledState] = useState<boolean>(() => {
    const soundOnValue = getUserScopedItem(LocalStorageKeys.SOUND_ON)
    // Default to OFF if no value exists, otherwise use the stored value
    return soundOnValue === null ? false : soundOnValue === 'true'
  })
  const [defaultTradingMode, setDefaultTradingModeState] = useState<DefaultTradingMode>(() => {
    const value = getUserScopedItem(LocalStorageKeys.DEFAULT_TRADING_MODE)
    return value === 'cash' || value === 'isolated' || value === 'last-used' ? value : 'last-used'
  })
  const [rememberSelectedLeverage, setRememberSelectedLeverageState] = useState<boolean>(() => {
    const value = getUserScopedItem(LocalStorageKeys.REMEMBER_SELECTED_LEVERAGE)
    return value === null ? true : value === 'true'
  })
  const [showOrdersAndEntriesOnChart, setShowOrdersAndEntriesOnChartState] = useState<boolean>(
    () => {
      const value = getUserScopedItem(LocalStorageKeys.SHOW_ORDERS_AND_ENTRIES_ON_CHART)
      return value === null ? true : value === 'true'
    },
  )

  // Wrapper to sync with localStorage
  const setSoundEnabled = (enabled: boolean) => {
    setSoundEnabledState(enabled)
    setUserScopedItem(LocalStorageKeys.SOUND_ON, enabled.toString())
  }

  const setHotkeysEnabled = (enabled: boolean) => {
    setHotkeysEnabledState(enabled)
    setUserScopedItem(LocalStorageKeys.HOTKEYS_ENABLED, enabled.toString())
  }

  const setDefaultTradingMode = (mode: DefaultTradingMode) => {
    setDefaultTradingModeState(mode)
    setUserScopedItem(LocalStorageKeys.DEFAULT_TRADING_MODE, mode)
  }

  const setRememberSelectedLeverage = (enabled: boolean) => {
    setRememberSelectedLeverageState(enabled)
    setUserScopedItem(LocalStorageKeys.REMEMBER_SELECTED_LEVERAGE, enabled.toString())
    // Remove the saved value so re-enabling this preference cannot restore a previous leverage setting.
    if (!enabled) removeUserScopedItem(LocalStorageKeys.LEVERAGE)
  }

  const setShowOrdersAndEntriesOnChart = (enabled: boolean) => {
    setShowOrdersAndEntriesOnChartState(enabled)
    setUserScopedItem(LocalStorageKeys.SHOW_ORDERS_AND_ENTRIES_ON_CHART, enabled.toString())
    window.localStorage.setItem('tvOrderLineFilter', enabled ? 'ALL' : 'NONE')
    window.localStorage.setItem('tvAveragePositionLineVisible', enabled.toString())
  }

  return (
    <SettingsContext.Provider
      value={{
        hotkeysEnabled,
        soundEnabled,
        defaultTradingMode,
        rememberSelectedLeverage,
        showOrdersAndEntriesOnChart,
        setHotkeysEnabled,
        setSoundEnabled,
        setDefaultTradingMode,
        setRememberSelectedLeverage,
        setShowOrdersAndEntriesOnChart,
      }}
    >
      {children}
    </SettingsContext.Provider>
  )
}

const useSettings = () => {
  const context = useContext(SettingsContext)
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider')
  }
  return context
}

export { SettingsProvider, useSettings }
